import { captureException, captureMessage } from './sentry';
import type { UnknownRecord } from '../types';

// Performance tracking types
interface PerformanceTimer {
  startTime: number;
  context?: UnknownRecord;
}

interface PerformanceMetrics {
  operation: string;
  duration: number;
  context?: UnknownRecord;
  timestamp: number;
}

// Global performance tracking store
const performanceTimers = new Map<string, PerformanceTimer>();
const performanceMetrics: PerformanceMetrics[] = [];

// Context management
let globalContext: UnknownRecord = {};

export const logger = {
  // Set global context for all logs
  setGlobalContext: (context: UnknownRecord) => {
    globalContext = { ...globalContext, ...context };
  },

  // Merge context with global context
  mergeContext: (context?: UnknownRecord): UnknownRecord => {
    return { ...globalContext, ...context };
  },

  // Start performance timer
  startTimer: (operation: string, context?: UnknownRecord): string => {
    const timerId = `${operation}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    performanceTimers.set(timerId, {
      startTime: performance.now(),
      context: context ? logger.mergeContext(context) : undefined,
    });

    logger.debug(`Started timer for operation: ${operation}`, { timerId, operation });
    return timerId;
  },

  // End performance timer and log metrics
  endTimer: (timerId: string, additionalContext?: UnknownRecord) => {
    const timer = performanceTimers.get(timerId);
    if (!timer) {
      logger.warn(`Timer not found: ${timerId}`);
      return;
    }

    const duration = performance.now() - timer.startTime;
    const operation = timerId.split('_')[0];

    const metrics: PerformanceMetrics = {
      operation,
      duration,
      context: { ...timer.context, ...additionalContext },
      timestamp: Date.now(),
    };

    performanceMetrics.push(metrics);
    performanceTimers.delete(timerId);

    // Keep only last 1000 metrics to prevent memory leaks
    if (performanceMetrics.length > 1000) {
      performanceMetrics.shift();
    }

    logger.info(`Completed operation: ${operation}`, {
      duration: `${duration.toFixed(2)}ms`,
      ...metrics.context,
    });

    return metrics;
  },

  // Get performance metrics
  getPerformanceMetrics: (operation?: string, limit = 100): PerformanceMetrics[] => {
    let metrics = performanceMetrics;
    if (operation) {
      metrics = metrics.filter(m => m.operation === operation);
    }
    return metrics.slice(-limit);
  },

  // Get average duration for operation
  getAverageDuration: (operation: string): number => {
    const metrics = performanceMetrics.filter(m => m.operation === operation);
    if (metrics.length === 0) return 0;
    const total = metrics.reduce((sum, m) => sum + m.duration, 0);
    return total / metrics.length;
  },

  // Log with performance context
  withTimer: async <T>(
    operation: string,
    fn: () => Promise<T>,
    context?: UnknownRecord
  ): Promise<T> => {
    const timerId = logger.startTimer(operation, context);
    try {
      const result = await fn();
      logger.endTimer(timerId);
      return result;
    } catch (error) {
      logger.endTimer(timerId, { error: error instanceof Error ? error.message : String(error) });
      throw error;
    }
  },

  error: (message: string | Error, context?: UnknownRecord) => {
    const mergedContext = logger.mergeContext(context);
    console.error(message, mergedContext);

    if (message instanceof Error) {
      captureException(message, mergedContext);
    } else {
      captureMessage(message, 'error', mergedContext);
    }
  },

  warn: (message: string, context?: UnknownRecord) => {
    const mergedContext = logger.mergeContext(context);
    console.warn(message, mergedContext);
    captureMessage(message, 'warning', mergedContext);
  },

  info: (message: string, context?: UnknownRecord) => {
    const mergedContext = logger.mergeContext(context);
    console.info(message, mergedContext);
    if (import.meta.env.DEV) {
      captureMessage(message, 'info', mergedContext);
    }
  },

  debug: (message: string, context?: UnknownRecord) => {
    const mergedContext = logger.mergeContext(context);
    console.debug(message, mergedContext);
    // Only log debug to Sentry in development
    if (import.meta.env.DEV) {
      captureMessage(message, 'debug', mergedContext);
    }
  },
};